import RestartAltIcon from '@mui/icons-material/RestartAlt';
import ClearIcon from '@mui/icons-material/Clear';
import SaveIcon from '@mui/icons-material/Save';
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  FormControlLabel,
  Paper,
  Stack,
  Switch,
  TextField,
  Typography,
} from '@mui/material';
import type { CellValueChangedEvent, ColDef } from 'ag-grid-community';
import { useMemo, useState } from 'react';
import { formatStockQuantity, splitBoxesPieces } from '@tiles-erp/shared';
import { usePagination } from '@tiles-erp/hooks';
import { PageContainer, useSaveShortcut } from '@tiles-erp/ui';
import type { StockBalanceItem } from '@tiles-erp/shared-types';
import { DataTable } from '../../components/DataTable';
import { SearchableSelect } from '../../components/SearchableSelect';
import { ApiError } from '../../lib/api-client';
import { useCatalogOptions } from '../catalog/api';
import { useProductPackageQuantities, useProductSizes } from '../products/api';
import { useBranches } from '../products/branch-prices-api';
import { useBulkSetStock, useCountSheet, useGodowns, useStockBalances } from './api';
import { useSessionBranchId } from '../../lib/session-branch';

interface CountEdit {
  boxes: number;
  pieces: number;
}

/** Grid row: the current balance plus the counted quantity being entered. */
type CountRow = StockBalanceItem & {
  countBoxes: number;
  countPieces: number;
  /** Difference in boxes between counted and current. */
  diffBoxes: number;
  /** Counted quantity expressed in square feet. */
  countSqft: number;
};

const round3 = (value: number): number => Math.round(value * 1000) / 1000;

const num = (value: unknown, fallback = 0): number => {
  const parsed = Number(value);
  return Number.isFinite(parsed) && parsed >= 0 ? parsed : fallback;
};

/**
 * Bulk stock count. Enter the physically counted boxes and loose pieces; saving posts
 * adjustment movements for the differences, so the ledger records what actually changed.
 */
export function StockCountPage(): JSX.Element {
  const pagination = usePagination({ initialPageSize: 100 });
  const branches = useBranches();
  const [branchId, setBranchId] = useSessionBranchId();
  const godowns = useGodowns(branchId || undefined);
  const [godownId, setGodownId] = useState('');
  const brands = useCatalogOptions('/brands');
  const [brandId, setBrandId] = useState('');
  const categories = useCatalogOptions('/categories', brandId || undefined);
  const [categoryId, setCategoryId] = useState('');
  const sizes = useProductSizes();
  const [sizeMm, setSizeMm] = useState('');
  const packageQuantities = useProductPackageQuantities();
  const [packageQty, setPackageQty] = useState('');
  const [reason, setReason] = useState('Physical stock count');

  const ready = Boolean(branchId && godownId);
  const [showAllProducts, setShowAllProducts] = useState(true);
  const filters = {
    branchId: branchId || undefined,
    godownId: godownId || undefined,
    brandId: brandId || undefined,
    categoryId: categoryId || undefined,
    sizeMm: sizeMm || undefined,
    piecesPerBox: packageQty || undefined,
  };
  const inStock = useStockBalances(pagination.query, filters, ready && !showAllProducts);
  const countSheet = useCountSheet(
    branchId || undefined,
    godownId || undefined,
    pagination.query,
    {
      brandId: brandId || undefined,
      categoryId: categoryId || undefined,
      sizeMm: sizeMm || undefined,
      piecesPerBox: packageQty || undefined,
    },
    ready && showAllProducts,
  );
  const { data, isFetching, refetch } = showAllProducts ? countSheet : inStock;
  const bulkSet = useBulkSetStock();

  const [edits, setEdits] = useState<Record<string, CountEdit>>({});
  const [error, setError] = useState<string | null>(null);
  const [summary, setSummary] = useState<string | null>(null);

  const items = data?.items ?? [];

  /** Stable identity for a stock key across product/batch/shade. */
  const rowKey = (item: StockBalanceItem): string =>
    `${item.productId}|${item.batchNo ?? ''}|${item.shade ?? ''}`;

  /** The product's conversion factor, supplied by the API for every row. */
  const piecesPerBox = (item: StockBalanceItem): number => item.piecesPerBox;

  const rows = useMemo<CountRow[]>(
    () =>
      items.map((item) => {
        const key = rowKey(item);
        const edit = edits[key];
        const ppb = piecesPerBox(item);
        // Piece-based products are counted purely in pieces; box-based products split
        // into whole boxes plus the loose remainder.
        const isPieceUom = item.baseUom === 'PIECE';
        // Count-sheet rows carry both branch total and selected-godown quantity.
        // The in-stock-only endpoint is already filtered to the selected godown, so
        // its own quantity is the correct fallback.
        const selectedQty = item.selectedGodownQtyBoxes ?? item.qtyBoxes;
        const split = splitBoxesPieces(selectedQty, ppb);
        const defaults = isPieceUom
          ? { boxes: 0, pieces: Math.round(selectedQty * ppb) }
          : split;
        const countBoxes = edit ? edit.boxes : defaults.boxes;
        const countPieces = edit ? edit.pieces : defaults.pieces;
        const target = round3(countBoxes + (ppb > 0 ? countPieces / ppb : 0));
        const sqftPerBox = item.qtyBoxes > 0 ? item.qtySqft / item.qtyBoxes : 0;
        return {
          ...item,
          countBoxes,
          countPieces,
          diffBoxes: round3(target - selectedQty),
          countSqft: round3(target * sqftPerBox),
        };
      }),
    [items, edits],
  );

  const onCellValueChanged = (event: CellValueChangedEvent<CountRow>): void => {
    const row = event.data;
    if (!row) return;
    setSummary(null);
    setEdits((prev) => ({
      ...prev,
      [rowKey(row)]: { boxes: num(row.countBoxes), pieces: num(row.countPieces) },
    }));
  };

  const updateCount = (row: CountRow, change: Partial<CountEdit>): void => {
    setSummary(null);
    setEdits((prev) => ({
      ...prev,
      [rowKey(row)]: {
        boxes: change.boxes ?? row.countBoxes,
        pieces: change.pieces ?? row.countPieces,
      },
    }));
  };

  const wasCounted = (row: CountRow): boolean => Object.hasOwn(edits, rowKey(row));
  const dirtyCount = rows.filter(wasCounted).length;

  const save = async (): Promise<void> => {
    setError(null);
    setSummary(null);
    if (!reason.trim()) {
      setError('Enter a reason for the count');
      return;
    }
    const changed = rows.filter(wasCounted);
    if (changed.length === 0) {
      setError('Enter the physically counted quantity for at least one product');
      return;
    }
    try {
      const result = await bulkSet.mutateAsync({
        branchId,
        godownId,
        reason: reason.trim(),
        lines: changed.map((row) => ({
          productId: row.productId,
          batchNo: row.batchNo,
          shade: row.shade,
          boxes: row.countBoxes,
          pieces: row.countPieces,
        })),
      });
      setEdits({});
      setSummary(
        `Posted ${result.posted} adjustment(s); ${result.unchanged} line(s) already matched.`,
      );
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Failed to post the count');
      void refetch();
    }
  };

  const columns = useMemo<ColDef<CountRow>[]>(
    () => [
      { field: 'sku', headerName: 'SKU', minWidth: 150 },
      { field: 'productName', headerName: 'Product', minWidth: 190 },
      {
        headerName: 'Current locations',
        maxWidth: 150,
        valueGetter: (p) => p.data?.sourceGodownCount ?? 0,
        cellStyle: (p) => ({
          fontWeight: (p.value as number) > 1 ? 700 : 400,
          color: (p.value as number) > 1 ? '#c62828' : 'inherit',
        }),
      },
      {
        headerName: 'This godown box',
        maxWidth: 130,
        valueGetter: (p) =>
          p.data && p.data.baseUom !== 'PIECE'
            ? splitBoxesPieces(p.data.selectedGodownQtyBoxes ?? p.data.qtyBoxes, p.data.piecesPerBox).boxes
            : '',
      },
      {
        headerName: 'This godown pcs',
        maxWidth: 130,
        valueGetter: (p) => {
          if (!p.data) return '';
          return p.data.baseUom === 'PIECE'
            ? Math.round((p.data.selectedGodownQtyBoxes ?? p.data.qtyBoxes) * p.data.piecesPerBox)
            : splitBoxesPieces(p.data.selectedGodownQtyBoxes ?? p.data.qtyBoxes, p.data.piecesPerBox).pieces;
        },
      },
      {
        headerName: 'Branch total',
        maxWidth: 130,
        valueGetter: (p) => p.data
          ? formatStockQuantity(
              p.data.qtyBoxes,
              p.data.piecesPerBox,
              p.data.qtyBoxes > 0 ? p.data.qtySqft / p.data.qtyBoxes : 0,
              p.data.baseUom,
            )
          : '',
      },
      {
        field: 'countBoxes',
        headerName: 'Counted box',
        editable: (p) => p.data?.baseUom !== 'PIECE',
        maxWidth: 140,
        valueParser: (p) => num(p.newValue, num(p.oldValue)),
        valueFormatter: (p) => (p.data?.baseUom === 'PIECE' ? '' : String(p.value ?? '')),
        cellStyle: (p) => ({
          backgroundColor:
            p.data?.baseUom === 'PIECE' ? 'transparent' : 'rgba(11, 95, 255, 0.06)',
        }),
      },
      {
        field: 'countPieces',
        headerName: 'Counted pcs',
        editable: true,
        maxWidth: 130,
        valueParser: (p) => num(p.newValue, num(p.oldValue)),
        cellStyle: { backgroundColor: 'rgba(11, 95, 255, 0.06)' },
      },
      {
        field: 'countSqft',
        headerName: 'Counted sq.ft',
        maxWidth: 140,
        valueFormatter: (p) => (p.value ? String(p.value) : ''),
      },
      {
        field: 'diffBoxes',
        headerName: 'Difference',
        maxWidth: 150,
        valueFormatter: (p) => {
          const delta = p.value as number;
          if (!delta) return '—';
          const row = p.data;
          const sign = delta > 0 ? '+' : '−';
          if (!row) return `${sign}${Math.abs(delta)}`;
          return `${sign}${formatStockQuantity(
            Math.abs(delta),
            row.piecesPerBox,
            row.qtyBoxes > 0 ? row.qtySqft / row.qtyBoxes : 0,
            row.baseUom,
          )}`;
        },
        cellStyle: (p) => ({
          fontWeight: 600,
          color:
            p.value === 0 ? 'inherit' : (p.value as number) > 0 ? '#1e874b' : '#c62828',
        }),
      },
    ],
    [godownId],
  );

  // Ctrl+S saves without reaching for the mouse.
  useSaveShortcut(() => void save());

  return (
    <PageContainer
      title="Stock count"
      subtitle="Count and update stock for the selected godown only."
      actions={
        <Stack direction="row" spacing={1} alignItems="center">
          {dirtyCount > 0 && <Chip label={`${dirtyCount} counted`} color="warning" size="small" />}
          <Button
            variant="outlined"
            startIcon={<RestartAltIcon />}
            disabled={Object.keys(edits).length === 0}
            onClick={() => {
              setEdits({});
              setError(null);
            }}
          >
            Discard
          </Button>
          <Button
            variant="contained"
            startIcon={<SaveIcon />}
            disabled={!ready || dirtyCount === 0 || bulkSet.isPending}
            onClick={() => void save()}
          >
            {bulkSet.isPending ? 'Saving…' : `Save verification ${dirtyCount || ''}`}
          </Button>
        </Stack>
      }
    >
      <Stack spacing={1}>
        {error && <Alert severity="error">{error}</Alert>}
        {summary && <Alert severity="success">{summary}</Alert>}
        <Alert severity="info">
          Enter the physical quantity in the selected godown. Stock in other godowns will
          remain unchanged; the branch total is shown only for reference.
        </Alert>

        <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
          <SearchableSelect label="Branch" value={branchId}
            options={(branches.data ?? []).map((branch) => ({ value: branch.id, label: branch.name }))}
            onChange={(nextBranchId) => {
              setBranchId(nextBranchId);
              setGodownId('');
              setEdits({});
            }}
            width={180} />
          <SearchableSelect label="Godown" value={godownId}
            options={(godowns.data ?? []).map((godown) => ({ value: godown.id, label: godown.name }))}
            onChange={(nextGodownId) => {
              setGodownId(nextGodownId);
              setEdits({});
            }}
            disabled={!branchId}
            width={230} />
          <SearchableSelect label="Brand" value={brandId} allLabel="All brands"
            options={(brands.data ?? []).map((brand) => ({ value: brand.id, label: brand.name }))}
            onChange={(value) => { setBrandId(value); setCategoryId(''); pagination.setPage(1); }} width={160} />
          <SearchableSelect label="Category" value={categoryId} allLabel="All categories"
            options={(categories.data ?? []).map((category) => ({ value: category.id, label: category.name }))}
            onChange={(value) => { setCategoryId(value); pagination.setPage(1); }} width={180} />
          <SearchableSelect label="Size" value={sizeMm} allLabel="All sizes"
            options={(sizes.data ?? []).map((size) => ({ value: size, label: size }))}
            onChange={(value) => { setSizeMm(value); pagination.setPage(1); }} width={140} />
          <SearchableSelect label="Package qty" value={packageQty} allLabel="All package qty"
            options={(packageQuantities.data ?? []).map((quantity) => ({ value: String(quantity), label: String(quantity) }))}
            onChange={(value) => { setPackageQty(value); pagination.setPage(1); }} width={150} />
          {(brandId || categoryId || sizeMm || packageQty) && (
            <Button size="small" color="inherit" startIcon={<ClearIcon />} onClick={() => {
              setBrandId(''); setCategoryId(''); setSizeMm(''); setPackageQty(''); pagination.setPage(1);
            }}>Clear filters</Button>
          )}
          <FormControlLabel
            control={
              <Switch
                size="small"
                checked={showAllProducts}
                onChange={(e) => {
                  setShowAllProducts(e.target.checked);
                  setEdits({});
                  pagination.setPage(1);
                }}
              />
            }
            label="Show all products"
          />
          <Divider orientation="vertical" flexItem />
          <TextField
            label="Reason"
            size="small"
            fullWidth={false}
            value={reason}
            onChange={(e) => setReason(e.target.value)}
            sx={{ width: 240 }}
          />
        </Stack>

        {!ready ? (
          <Alert severity="info">Select a branch and godown to count its stock.</Alert>
        ) : (
          <DataTable
            rows={rows}
            columns={columns}
            meta={data?.meta}
            pagination={pagination}
            loading={isFetching}
            searchPlaceholder="Search by product or SKU…"
            height={600}
            mobileRowRenderer={(row) => (
              <Paper variant="outlined" sx={{ p: 1.5, borderRadius: 2 }}>
                <Stack spacing={1.25}>
                  <Box>
                    <Typography variant="subtitle1" fontWeight={700}>
                      {row.productName}
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                      {row.sku}
                      {row.batchNo ? ` · Batch ${row.batchNo}` : ''}
                      {row.shade ? ` · ${row.shade}` : ''}
                    </Typography>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                      gap: 1,
                    }}
                  >
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Current stock</Typography>
                      <Typography variant="body2" fontWeight={700}>
                        {formatStockQuantity(
                          row.qtyBoxes,
                          row.piecesPerBox,
                          row.qtyBoxes > 0 ? row.qtySqft / row.qtyBoxes : 0,
                          row.baseUom,
                        )}
                      </Typography>
                    </Box>
                    <Box sx={{ p: 1, bgcolor: 'action.hover', borderRadius: 1.5 }}>
                      <Typography variant="caption" color="text.secondary">Difference</Typography>
                      <Typography
                        variant="body2"
                        fontWeight={700}
                        color={row.diffBoxes === 0 ? 'text.primary' : row.diffBoxes > 0 ? 'success.main' : 'error.main'}
                      >
                        {row.diffBoxes === 0
                          ? 'No change'
                          : `${row.diffBoxes > 0 ? '+' : '−'}${formatStockQuantity(
                              Math.abs(row.diffBoxes),
                              row.piecesPerBox,
                              row.qtyBoxes > 0 ? row.qtySqft / row.qtyBoxes : 0,
                              row.baseUom,
                            )}`}
                      </Typography>
                    </Box>
                  </Box>

                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: row.baseUom === 'PIECE' ? '1fr' : 'repeat(2, minmax(0, 1fr))',
                      gap: 1,
                    }}
                  >
                    {row.baseUom !== 'PIECE' && (
                      <TextField
                        label="Counted boxes"
                        type="number"
                        value={row.countBoxes}
                        onChange={(event) => updateCount(row, { boxes: num(event.target.value) })}
                        inputProps={{ min: 0, inputMode: 'decimal' }}
                        sx={{ '& .MuiInputBase-root': { minHeight: 50, fontSize: 16 } }}
                      />
                    )}
                    <TextField
                      label="Counted pieces"
                      type="number"
                      value={row.countPieces}
                      onChange={(event) => updateCount(row, { pieces: num(event.target.value) })}
                      inputProps={{ min: 0, inputMode: 'numeric' }}
                      sx={{ '& .MuiInputBase-root': { minHeight: 50, fontSize: 16 } }}
                    />
                  </Box>
                </Stack>
              </Paper>
            )}
            gridOptions={{
              getRowId: (p) => `${p.data.productId}|${p.data.batchNo ?? ''}|${p.data.shade ?? ''}`,
              onCellValueChanged,
              stopEditingWhenCellsLoseFocus: true,
              singleClickEdit: true,
            }}
          />
        )}
      </Stack>
    </PageContainer>
  );
}
